import React, { useEffect, useState } from 'react';
import { DcmSegmentMaster } from '../../../types/knowledge';
import {
  buildSegmentFromContent,
  generateSegmentContent,
} from '../../../services/api/knowledgeStrategyGeneration';

// DCMモック(knowledge-dcm-scoped.html #segAddModal)準拠のローディング文言
const LOADING_MESSAGES = [
  '基本情報・競合を分析中',
  '未開拓の顧客群を探索中',
  'セグメントの輪郭を言語化中',
];

interface SegmentGenerateModalProps {
  isOpen: boolean;
  productId: number;
  nextOrder: number;
  onClose: () => void;
  onCreate: (segment: DcmSegmentMaster) => void;
}

const SegmentGenerateModal: React.FC<SegmentGenerateModalProps> = ({
  isOpen,
  productId,
  nextOrder,
  onClose,
  onCreate,
}) => {
  const [mode, setMode] = useState<'auto' | 'manual'>('auto');
  const [instruction, setInstruction] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [messageIndex, setMessageIndex] = useState(0);

  useEffect(() => {
    if (!isGenerating) return undefined;
    const timer = window.setInterval(() => {
      // モック準拠: 最後の文言で止める（先頭へは折り返さない）
      setMessageIndex((current) => Math.min(current + 1, LOADING_MESSAGES.length - 1));
    }, 400);
    return () => window.clearInterval(timer);
  }, [isGenerating]);

  const handleCreate = async () => {
    setMessageIndex(0);
    setIsGenerating(true);
    try {
      const content = await generateSegmentContent(productId, mode, instruction.trim(), {
        next_order: nextOrder,
      });
      onCreate(buildSegmentFromContent(content, nextOrder));
    } finally {
      setIsGenerating(false);
      setInstruction('');
      setMode('auto');
      onClose();
    }
  };

  if (!isOpen) return null;

  // モック .padd-mode 準拠のモード選択カード
  const renderModeCard = (
    value: 'auto' | 'manual',
    icon: string,
    title: string,
    description: string,
  ) => {
    const selected = mode === value;
    return (
      <button
        type="button"
        onClick={() => setMode(value)}
        className={`flex w-full items-start gap-[11px] rounded-xl border-[1.5px] px-[14px] py-3 text-left transition-colors ${
          selected
            ? 'border-primary bg-[rgba(97,111,201,.04)]'
            : 'border-[#E4E4E7] bg-white hover:border-[#C7CCEF]'
        }`}
      >
        <span className="material-icons-round mt-[1px] flex-none text-[20px] text-primary">{icon}</span>
        <span className="min-w-0">
          <span className="block text-[13px] font-bold">{title}</span>
          <span className="mt-[2px] block text-[11px] leading-[1.5] text-[#8A90A4]">{description}</span>
        </span>
      </button>
    );
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-[rgba(20,18,28,.42)] p-6"
      onClick={() => {
        if (!isGenerating) onClose();
      }}
      role="presentation"
    >
      <div
        className="w-full max-w-[520px] overflow-hidden rounded-[18px] bg-white shadow-[0_30px_80px_rgba(0,0,0,.3)]"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="セグメントを追加"
      >
        {isGenerating ? (
          <div className="px-6 py-[42px] text-center">
            <span className="material-icons-round inline-block animate-spin text-[30px] text-primary">sync</span>
            <div className="mt-3 text-[14.5px] font-extrabold text-[#1D1927]">AI がセグメントを生成しています…</div>
            <div className="mt-[5px] text-[12px] text-[#8A90A4]">{LOADING_MESSAGES[messageIndex]}</div>
          </div>
        ) : (
          <>
            <h3 className="flex items-center gap-2 px-[22px] pt-[18px] text-[16px] font-extrabold text-[#1D1927]">
              <span className="material-icons-round text-[19px] text-primary">view_week</span>
              セグメントを追加
            </h3>
            <div className="px-[22px] pt-3 text-[13px] leading-[1.7] text-[#4A4D60]">
              <div className="flex flex-col gap-[9px]">
                {renderModeCard('auto', 'auto_awesome', 'AI におまかせ', '基本情報・競合・既存セグメントから、有望な顧客群を提案')}
                {renderModeCard('manual', 'edit_note', '指示を出して生成', '狙いたい顧客群を指定して追加')}
              </div>
              {mode === 'manual' && (
                <div>
                  <div className="mb-1 mt-[14px] text-[11px] font-bold text-[#6B7280]">AI への指示</div>
                  <textarea
                    value={instruction}
                    onChange={(event) => setInstruction(event.target.value)}
                    rows={3}
                    placeholder="例：訪日外国人の観光客。英語対応・キャッシュレス重視で、SNSで宿を探す層。"
                    className="box-border w-full resize-y rounded-[10px] border-[1.5px] border-[#E4E4E7] px-3 py-[10px] text-[12.5px] outline-none [font-family:inherit] focus:border-primary"
                  />
                </div>
              )}
            </div>
            <div className="flex justify-end gap-[9px] px-[22px] pb-5 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center gap-[5px] rounded-lg border border-[#E4E4E7] bg-white px-[11px] py-[5px] text-[12px] font-semibold text-[#4B5563] transition-colors hover:border-[#AEB5DE] hover:bg-[#F5F6FC] hover:text-[#4F5BB5]"
              >
                キャンセル
              </button>
              <button
                type="button"
                onClick={() => { void handleCreate(); }}
                className="inline-flex items-center gap-[6px] rounded-lg bg-primary px-[13px] py-[6px] text-[13px] font-semibold text-white transition-colors hover:bg-primary-hover"
              >
                <span className="material-icons-round text-[15px]">auto_awesome</span>
                セグメントを生成
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SegmentGenerateModal;
